/*
 * Figma `121:1133` — LIVE-01 · Check-in · Live scan + exceptions.
 *
 * `OperationsTemplate`: the outcome of the last scan in the largest type on the page, then the
 * scanner, then the record. That order is the template's own rule and it is right here — during a
 * live gate nobody scrolls, so "did that work" has to be where the eye already is.
 *
 * **What the frame asks for that does not exist.** Its right-hand column is an exception queue with
 * rows per gate — สแกนซ้ำ, ชื่อไม่ตรง, บัตรถูกยกเลิก, Offline sync — each with a state and an action.
 * Four separate things are missing behind that one panel:
 *
 *   - **Gates.** There is no gate or lane anywhere in the schema. Every "Gate A/B/C/D" in the frame,
 *     and the "+18% หลังเปิด Gate B" under its rate tile, has nothing to read.
 *   - **An exception queue.** A refusal here is an answer to one request, shown once and gone.
 *     Nothing is persisted, nothing is assigned, and nothing can be picked up by someone else.
 *   - **Devices.** "Scanner online 22/24" needs a device registry. There is none; a scanner is
 *     whatever browser is pointed at this page.
 *   - **Offline.** The desk is online-only. There is no local queue and no sync, so an "Offline
 *     sync · กำลัง sync" row would be describing a mechanism that cannot run.
 *
 * Drawing any of it would be worse here than anywhere else in the redesign: this is the screen
 * someone stares at while a queue builds, and a panel that looks like a work queue but holds
 * nothing is a panel people will wait on.
 *
 * What is real — and what the frame leaves out — is the confirmation. TICK-062 R8: a QR admits a
 * whole order exactly once, there is no partial admission, and a group order takes an explicit
 * acknowledgement that everyone is present. That is the screen's actual consequence and it is kept
 * in full, wristbands and custom fields included.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Card, FeedbackState, KpiCard, OperationsTemplate, StatusBadge, TextField,
} from '../..';
import type { BadgeTone } from '../..';
import type { Destination } from '../../destinations';
import QrScanner from '../../../components/QrScanner';
import type { BundleStatus, CheckinState, ScanOutcome } from './useCheckin';
import { RATE_WINDOW_MINUTES, useCheckin } from './useCheckin';
import { BUNDLE_STALE_HOURS } from './offlineCheckin';

const OUTCOME_TONE: Record<ScanOutcome['kind'], BadgeTone> = {
  admitted: 'success', provisional: 'warning', already: 'danger', unverified: 'warning', error: 'danger',
};
const OUTCOME_WORD: Record<ScanOutcome['kind'], string> = {
  admitted: 'เข้างานได้',
  provisional: 'เข้างานได้ (ยังไม่ได้ส่งขึ้นระบบ)',
  already: 'ใช้ไปแล้ว',
  unverified: 'ยังไม่ยืนยันการชำระเงิน',
  error: 'ไม่สำเร็จ',
};

const time = (iso: string) => new Date(iso).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });

export function CheckinScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: CheckinState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    pending, outcome, log, stats, rate, busy, confirming, acknowledged,
    setAcknowledged, lookup, confirm, cancel, clearOutcome,
    online, bundle, bundleStatus, bundleAge, queue, queuePersisted, exceptions, syncing,
    refreshBundle, syncNow, dismissException,
  } = state;

  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  /*
   * A keyboard scanner types into whatever has focus, so the desk has to hold focus in the code box
   * between scans — the box is autofocused when it mounts, and focus is put back after every
   * outcome, because the scan that follows a refusal is the one most likely to be rushed.
   *
   * Mount focus is `autoFocus` on the field rather than a `focus()` call in this effect: the effect
   * fires while the nav button that opened the screen is still taking focus from the click, and the
   * button wins. React applies `autoFocus` as part of the commit, which does not race it.
   */
  useEffect(() => {
    if (!pending && !scanning && outcome) codeRef.current?.focus();
  }, [pending, scanning, outcome]);

  const checkedIn = stats?.checkin.checkedIn ?? 0;
  const totalPaid = stats?.checkin.totalPaid ?? 0;
  // TICK-153 — the desk hands out wristbands per person, so people lead and orders follow.
  const peopleIn = stats?.checkin.peopleCheckedIn ?? checkedIn;
  const peoplePaid = stats?.checkin.peoplePaid ?? totalPaid;
  const remaining = Math.max(0, peoplePaid - peopleIn);

  const submit = () => {
    const value = code.trim();
    if (!value) return;
    setCode('');
    void lookup(value);
  };

  return (
    <OperationsTemplate
      gate={{ destination, permitted, role }}
      kpis={
        <>
          <KpiCard
            label="เช็กอินแล้ว (คน)"
            value={peopleIn.toLocaleString('th-TH')}
            meta={peoplePaid > 0
              ? `${((peopleIn / peoplePaid) * 100).toFixed(1)}% ของ ${peoplePaid.toLocaleString('th-TH')} คนที่ชำระแล้ว · ${checkedIn.toLocaleString('th-TH')}/${totalPaid.toLocaleString('th-TH')} ออร์เดอร์`
              : 'ยังไม่มีออร์เดอร์ที่ชำระเงินแล้ว'}
          />
          <KpiCard
            label="ยังไม่มาถึง (คน)"
            value={remaining.toLocaleString('th-TH')}
            meta={`จาก ${Math.max(0, totalPaid - checkedIn).toLocaleString('th-TH')} ออร์เดอร์ที่ชำระแล้วและยังไม่ได้เช็กอิน`}
          />
          <KpiCard
            label={`ต่อนาที (${RATE_WINDOW_MINUTES} นาทีล่าสุด)`}
            value={rate.perMinute === null ? '—' : String(rate.perMinute)}
            meta={
              rate.perMinute === null
                ? `ยังไม่มีการเช็กอินใน ${RATE_WINDOW_MINUTES} นาทีที่ผ่านมา`
                : rate.saturated
                  ? `อย่างน้อย ${rate.counted} คน — บันทึกส่งมาเต็มโควตา ตัวเลขจริงอาจสูงกว่านี้`
                  : `${rate.counted} คนใน ${RATE_WINDOW_MINUTES} นาที`
            }
          />
          <KpiCard
            label="คนที่เช็กอินให้"
            value={(log?.byStaff.length ?? 0).toLocaleString('th-TH')}
            meta={log && log.byStaff.length > 0
              ? `มากสุด ${log.byStaff[0].actor} ${log.byStaff[0].count.toLocaleString('th-TH')} รายการ`
              : 'ยังไม่มีใครเช็กอินในงานนี้'}
          />
        </>
      }
      result={
        <>
          <OfflineBanner
            online={online}
            bundleStatus={bundleStatus}
            bundleAge={bundleAge}
            orderCount={bundle?.orderCount ?? 0}
            queued={queue.length}
            queuePersisted={queuePersisted}
            syncing={syncing}
            onRefresh={() => void refreshBundle()}
            onSync={() => void syncNow()}
          />
          {outcome && <Outcome outcome={outcome} onDismiss={clearOutcome} />}
        </>
      }
      focus={
        pending ? (
          <Card title="ตรวจสอบก่อนแลกริสแบนด์">
            <p className="jt-scan-code">{pending.code}</p>
            <p className="jt-scan-name">{pending.name || '—'}</p>
            <p className="jt-field-hint">{pending.contact}</p>

            <ul className="jt-checks">
              {pending.lines.map((line) => (
                <li key={line.key} className="jt-check">
                  <div className="jt-check-copy">
                    <p className="jt-check-label">{line.label}</p>
                    {line.custom && <p className="jt-check-detail jt-mono">{line.custom}</p>}
                  </div>
                </li>
              ))}
            </ul>

            <p className="jt-field-hint jt-mono">
              รวม {pending.qty} ใบ · เข้างานได้ {pending.entries} คน
            </p>

            {pending.wristbands.length > 0 && (
              <Card title="ต้องแจก Wristband">
                <ul className="jt-checks">
                  {pending.wristbands.map((w) => (
                    <li key={w.key} className="jt-check">
                      <div className="jt-check-copy">
                        <p className="jt-check-label">{w.name}</p>
                        <p className="jt-check-detail">{w.color}</p>
                      </div>
                      <span className="jt-check-trail jt-mono">× {w.qty}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/*
              TICK-062 R8 — the same whole-order acknowledgement the check-in app shows. Admin is a
              convenience surface for the same physical act; if it were the lax way to confirm, the
              contract would only hold for whoever used the other screen.
            */}
            <Alert tone="warning" title="เช็กอินทั้งคำสั่งซื้อครั้งเดียว">
              <p>
                QR นี้ใช้ได้ครั้งเดียวสำหรับทั้งคำสั่งซื้อ — ยืนยันแล้วจะใช้ซ้ำไม่ได้
                มอบ Wristband ตามรายการด้านบนให้ผู้ถือ QR ไปทั้งหมด — ไม่ต้องรอให้ทุกคนในออเดอร์มาถึง
              </p>
              {pending.entries > 1 && (
                <label className="jt-ack">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  <span>มอบ Wristband สำหรับ {pending.entries} คน ให้ผู้ถือ QR ครบแล้ว</span>
                </label>
              )}
            </Alert>

            <div className="jt-drawer-guard">
              <Button
                variant="primary"
                disabled={confirming || (pending.entries > 1 && !acknowledged)}
                onClick={() => void confirm()}
              >
                {confirming ? 'กำลังบันทึก…' : `ยืนยันมอบ Wristband สำหรับ ${pending.entries} คน`}
              </Button>
              <Button variant="ghost" disabled={confirming} onClick={cancel}>ยกเลิก</Button>
            </div>
          </Card>
        ) : (
          <Card title="รับ QR หรือรหัสบัตร" sub="รองรับกล้อง เครื่องสแกนแบบคีย์บอร์ด และการพิมพ์เอง">
            <div className="jt-scan-row">
              <TextField
                ref={codeRef}
                /* eslint-disable-next-line jsx-a11y/no-autofocus -- single-purpose operational
                   screen whose only input is the scan target; see the note above. */
                autoFocus
                label="รหัสบัตร"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder="เช่น MF26-XXXXX"
                autoComplete="off"
                hint="เครื่องสแกนแบบคีย์บอร์ดจะพิมพ์ลงช่องนี้เองเมื่อช่องนี้ถูกโฟกัสอยู่"
              />
              {/* Disabled on an empty field, because `submit` returns early on one: an enabled
                  button that cannot act is the same thing as a button that does nothing. */}
              <Button variant="primary" disabled={busy || !code.trim()} onClick={submit}>
                {busy ? 'กำลังค้นหา…' : 'ค้นหา'}
              </Button>
            </div>

            {scanning ? (
              <>
                <QrScanner onDetected={(payload) => void lookup(payload)} />
                <Button variant="ghost" onClick={() => setScanning(false)}>ปิดกล้อง</Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => { clearOutcome(); setScanning(true); }}>
                เปิดกล้องสแกน QR
              </Button>
            )}
          </Card>
        )
      }
      context={
        <>
          <Card title="เช็กอินล่าสุด" sub="จากบันทึกของงานนี้ ล่าสุดก่อน">
            {!log || log.entries.length === 0 ? (
              <FeedbackState state="empty" title="ยังไม่มีการเช็กอิน">
                รายการจะขึ้นที่นี่ทันทีที่มีคนแรกผ่านประตู
              </FeedbackState>
            ) : (
              <ul className="jt-checks">
                {log.entries.slice(0, 12).map((entry) => (
                  <li key={entry.id} className="jt-check">
                    <div className="jt-check-copy">
                      <p className="jt-check-label jt-mono">{entry.detail}</p>
                      <p className="jt-check-detail">{entry.actor}</p>
                    </div>
                    <span className="jt-check-trail jt-mono">{time(entry.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {exceptions.length > 0 && (
            /*
             * The one thing degraded offline cannot prevent: two gates offline admitting one order.
             * The server refuses the second replay, and the refusal lands here rather than
             * disappearing — somebody has to decide what happened to the person who was let in.
             */
            <Card
              title={`ต้องให้คนตัดสิน ${exceptions.length} รายการ`}
              sub="รายการที่เช็กอินตอนออฟไลน์ไว้ แต่ระบบไม่รับตอนส่งขึ้น"
            >
              <ul className="jt-checks">
                {exceptions.map((item) => (
                  <li key={item.id} className="jt-check">
                    <div className="jt-check-copy">
                      <p className="jt-check-label jt-mono">{item.code}</p>
                      <p className="jt-check-detail">
                        {item.name} · เช็กอินไว้ตอน {time(item.admittedAt)}
                      </p>
                      <p className="jt-check-detail">{item.detail}</p>
                      <Button size="sm" variant="ghost" onClick={() => dismissException(item.id)}>
                        จัดการแล้ว
                      </Button>
                    </div>
                    <span className="jt-check-trail">
                      <StatusBadge tone={item.kind === 'conflict' ? 'danger' : 'warning'}>
                        {item.kind === 'conflict' ? 'ซ้ำ' : 'ระบบปฏิเสธ'}
                      </StatusBadge>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/*
            Said on the screen, not only in the resume doc. Someone comparing this with the frame
            will look for these, and "it is not built" is a better answer than an empty panel.
          */}
          <Alert tone="warning" title="สิ่งที่หน้านี้ยังทำไม่ได้">
            <ul className="jt-consequences">
              <li>ไม่มีการแยกประตู — ระบบยังไม่มีข้อมูลประตูหรือช่องทางเข้า ตัวเลขทั้งหมดจึงเป็นของทั้งงาน</li>
              <li>ไม่มีสถานะเครื่องสแกน — เครื่องสแกนคือเบราว์เซอร์ที่เปิดหน้านี้ ไม่มีทะเบียนอุปกรณ์ให้ตรวจ</li>
              {/*
                The reconciliation list above is not the general exception queue: it holds offline
                admissions the server refused, not every refusal. A scan denied while online is
                still an answer shown once, with nobody to hand it to.
              */}
              <li>การปฏิเสธตอนออนไลน์ยังไม่ถูกเก็บเป็นคิว — เห็นครั้งเดียวตอนสแกน ส่งต่อให้หัวหน้างานมาตามไม่ได้</li>
              <li>ตอนออฟไลน์จะไม่เห็นการคืนเงิน การยกเลิกบัตร หรือการเช็กอินจากเครื่องอื่นที่เกิดหลังจากดึงสำเนารายชื่อ</li>
            </ul>
          </Alert>
        </>
      }
    />
  );
}

/**
 * What the network is doing, above everything the operator can press.
 *
 * It sits at the top because it changes what every control below it means: the same scan either
 * admits somebody or records that somebody was admitted. A gate that cannot tell those apart is a
 * gate whose staff will not know whether to worry.
 */
function OfflineBanner({
  online, bundleStatus, bundleAge, orderCount, queued, queuePersisted, syncing, onRefresh, onSync,
}: {
  online: boolean;
  bundleStatus: BundleStatus;
  bundleAge: number | null;
  orderCount: number;
  queued: number;
  queuePersisted: boolean;
  syncing: boolean;
  onRefresh: () => void;
  onSync: () => void;
}) {
  const stale = bundleAge !== null && bundleAge >= BUNDLE_STALE_HOURS;

  if (!online) {
    return (
      <Alert tone="danger" title="ออฟไลน์อยู่ — เช็กอินต่อได้ แต่ยังไม่ได้ส่งขึ้นระบบ">
        <ul className="jt-consequences">
          <li>
            {bundleStatus === 'ready'
              ? `ใช้สำเนารายชื่อ ${orderCount.toLocaleString('th-TH')} ออร์เดอร์ในเครื่องนี้${stale ? ` (ดึงมา ${Math.round(bundleAge!)} ชม.แล้ว)` : ''}`
              : 'เครื่องนี้ไม่มีสำเนารายชื่อ — เช็กอินตอนออฟไลน์ไม่ได้'}
          </li>
          <li>จะไม่เห็นการคืนเงิน การยกเลิกบัตร หรือการเช็กอินจากเครื่องอื่นที่เกิดหลังจากดึงสำเนา</li>
          {queued > 0 && <li>รอส่งขึ้นระบบ {queued.toLocaleString('th-TH')} รายการ — จะส่งเองเมื่อเน็ตกลับมา</li>}
          {!queuePersisted && (
            <li><strong>เครื่องนี้บันทึกคิวลงดิสก์ไม่ได้ — ถ้าปิดหรือรีเฟรชหน้านี้ รายการที่รออยู่จะหาย</strong></li>
          )}
        </ul>
      </Alert>
    );
  }

  if (queued > 0) {
    return (
      <Alert tone="warning" title={`รอส่งขึ้นระบบ ${queued.toLocaleString('th-TH')} รายการ`}>
        <p>เน็ตกลับมาแล้ว — รายการที่เช็กอินตอนออฟไลน์จะถูกส่งขึ้นระบบและตรวจว่าซ้ำกับเครื่องอื่นหรือไม่</p>
        <div className="jt-drawer-guard">
          <Button size="sm" variant="primary" disabled={syncing} onClick={onSync}>
            {syncing ? 'กำลังส่ง…' : 'ส่งขึ้นระบบตอนนี้'}
          </Button>
        </div>
      </Alert>
    );
  }

  if (bundleStatus === 'failed') {
    return (
      <Alert tone="warning" title="ยังไม่มีสำเนารายชื่อสำหรับใช้ตอนเน็ตหลุด">
        <p>ถ้าเน็ตหลุดตอนนี้ เครื่องนี้จะเช็กอินต่อไม่ได้ — ลองดึงสำเนาอีกครั้งก่อนเปิดประตู</p>
        <div className="jt-drawer-guard">
          <Button size="sm" variant="secondary" onClick={onRefresh}>ดึงสำเนารายชื่อ</Button>
        </div>
      </Alert>
    );
  }

  if (stale) {
    return (
      <Alert tone="warning" title="สำเนารายชื่อในเครื่องนี้เก่าแล้ว">
        <p>
          ดึงมา {Math.round(bundleAge!)} ชั่วโมงแล้ว — ถ้าเน็ตหลุด จะไม่เห็นบัตรที่ขายหรือยกเลิกหลังจากนั้น
        </p>
        <div className="jt-drawer-guard">
          <Button size="sm" variant="secondary" onClick={onRefresh}>ดึงใหม่</Button>
        </div>
      </Alert>
    );
  }

  return null;
}

function Outcome({ outcome, onDismiss }: { outcome: ScanOutcome; onDismiss: () => void }) {
  return (
    <div className="jt-outcome" data-kind={outcome.kind} role="status" aria-live="assertive">
      <div className="jt-outcome-head">
        <StatusBadge tone={OUTCOME_TONE[outcome.kind]}>{OUTCOME_WORD[outcome.kind]}</StatusBadge>
        <Button size="sm" variant="ghost" onClick={onDismiss}>ล้าง</Button>
      </div>
      {outcome.kind === 'admitted' && (
        <>
          <p className="jt-outcome-code">{outcome.code}</p>
          <p className="jt-outcome-detail">{outcome.name} · {outcome.lines}</p>
          <p className="jt-outcome-detail">เข้างานได้ {outcome.entries} คน</p>
        </>
      )}
      {outcome.kind === 'already' && (
        <>
          <p className="jt-outcome-code">{outcome.code}</p>
          <p className="jt-outcome-detail">{outcome.detail}</p>
        </>
      )}
      {outcome.kind === 'unverified' && (
        <>
          <p className="jt-outcome-code">{outcome.code}</p>
          <p className="jt-outcome-detail">{outcome.name} · รอ admin ตรวจสลิปก่อน จึงยังเข้างานไม่ได้</p>
        </>
      )}
      {outcome.kind === 'provisional' && (
        <>
          <p className="jt-outcome-code">{outcome.code}</p>
          <p className="jt-outcome-detail">{outcome.name} · เข้างานได้ {outcome.entries} คน</p>
          <p className="jt-outcome-detail">
            บันทึกไว้ในเครื่องนี้แล้ว · รอส่งขึ้นระบบ {outcome.queued.toLocaleString('th-TH')} รายการ
          </p>
        </>
      )}
      {outcome.kind === 'error' && <p className="jt-outcome-detail">{outcome.detail}</p>}
    </div>
  );
}

export function CheckinDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useCheckin(token, eventSlug);
  return <CheckinScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
